import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { parseISO, differenceInMinutes } from 'date-fns';

/**
 * BoardQuickAccess - Hyper-simple QR code landing page
 *
 * This component provides a streamlined interface for users who scan a QR code
 * to quickly check in, make a reservation, or view the board's schedule.
 *
 * Design: Follows DESIGN_GUIDELINES.md with retro-modern aesthetic
 */
const BoardQuickAccess = ({ boardId: boardIdProp }) => {
  const params = useParams();
  const boardId = boardIdProp || params.boardId;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [boardInfo, setBoardInfo] = useState(null);
  const [error, setError] = useState(null);
  const [checkingIn, setCheckingIn] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  useEffect(() => {
    fetchBoardQuickInfo();
  }, [boardId]);

  const fetchBoardQuickInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      const response = await fetch(`${API_URL}/api/boards/${boardId}/quick-info`, {
        headers
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to load board information');
      }

      const data = await response.json();
      setBoardInfo(data);
    } catch (err) {
      console.error('Error fetching board info:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async () => {
    if (!boardInfo?.nextReservation?.checkInToken) {
      return;
    }

    setCheckingIn(true);
    try {
      const response = await fetch(
        `${API_URL}/api/reservations/checkin/token/${boardInfo.nextReservation.checkInToken}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to check in');
      }

      // Refresh board info to show updated status
      await fetchBoardQuickInfo();
    } catch (err) {
      alert(err.message);
    } finally {
      setCheckingIn(false);
    }
  };

  const handleMakeReservation = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      // Redirect to login with return URL
      navigate(`/login?returnTo=/board/${boardId}`);
      return;
    }
    // Navigate to full board view with reservation capabilities
    navigate(`/board/${boardId}`);
  };

  const handleViewSchedule = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      // Redirect to login with return URL
      navigate(`/login?returnTo=/board/${boardId}`);
      return;
    }
    // Navigate to full board view
    navigate(`/board/${boardId}`);
  };

  const formatTimeUntil = (startTime) => {
    if (!startTime) return null;

    const now = new Date();
    const start = parseISO(startTime);
    const minutesUntil = differenceInMinutes(start, now);

    if (minutesUntil < 0) {
      return 'Now';
    } else if (minutesUntil < 60) {
      return `${minutesUntil} minute${minutesUntil === 1 ? '' : 's'}`;
    } else if (minutesUntil < 1440) { // Less than 24 hours
      const hours = Math.floor(minutesUntil / 60);
      const mins = minutesUntil % 60;

      if (mins === 0) {
        return `${hours} hour${hours === 1 ? '' : 's'}`;
      } else {
        return `${hours} hour${hours === 1 ? '' : 's'} and ${mins} minute${mins === 1 ? '' : 's'}`;
      }
    } else {
      const days = Math.floor(minutesUntil / 1440);
      return `${days} day${days === 1 ? '' : 's'}`;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-purple-50 to-magenta-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 border-2 border-slate-900 shadow-retro max-w-md w-full text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-slate-200 border-t-teal-500 mx-auto"></div>
          <p className="mt-6 text-slate-700 font-semibold">Loading board...</p>
        </div>
      </div>
    );
  }

  if (error || !boardInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-purple-50 to-magenta-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 border-2 border-slate-900 shadow-retro max-w-md w-full text-center">
          <div className="w-20 h-20 bg-coral-100 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-coral-500">
            <svg className="w-10 h-10 text-coral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-3">Board Not Found</h1>
          <p className="text-slate-600 mb-8">{error || 'This board could not be loaded'}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full px-6 py-4 bg-teal-500 hover:bg-teal-600 text-white font-bold rounded-lg border-2 border-slate-900 shadow-retro hover:shadow-retro-hover transition-all touch-manipulation"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const { board, nextReservation, canCheckIn } = boardInfo;
  const timeUntil = nextReservation ? formatTimeUntil(nextReservation.startTime) : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-purple-50 to-magenta-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 border-2 border-slate-900 shadow-retro max-w-md w-full">

        {/* Header with geometric accent */}
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2 flex items-center justify-center gap-3">
            <span className="w-1.5 h-10 bg-gradient-to-b from-teal-500 to-purple-500 rounded"></span>
            {board.name}
          </h1>
          <p className="text-lg text-slate-600 font-semibold">{board.gymName}</p>
        </div>

        {/* Next Reservation Countdown */}
        {nextReservation && timeUntil && (
          <div className="mb-8 bg-gradient-to-br from-teal-50 to-purple-50 rounded-xl p-6 border-2 border-slate-900">
            <div className="text-center">
              <p className="text-sm font-bold text-slate-600 uppercase tracking-wide mb-2">
                Next Reservation
              </p>
              <p className="text-3xl font-bold bg-gradient-to-r from-teal-600 via-purple-600 to-magenta-600 bg-clip-text text-transparent">
                {timeUntil === 'Now' ? 'Now' : `in ${timeUntil}`}
              </p>
              {nextReservation.isCurrentUser && (
                <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-sunshine-100 text-sunshine-800 border-2 border-sunshine-500 rounded-full font-bold text-sm">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  Your Reservation
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-4">

          {/* Check In Button - Only show if user has an upcoming reservation they can check into */}
          {canCheckIn && nextReservation?.isCurrentUser && (
            <button
              onClick={handleCheckIn}
              disabled={checkingIn}
              className="w-full px-6 py-5 bg-teal-500 hover:bg-teal-600 text-white font-bold text-lg rounded-lg border-2 border-slate-900 shadow-retro hover:shadow-retro-hover transition-all disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation flex items-center justify-center gap-3"
            >
              {checkingIn ? (
                <>
                  <div className="animate-spin rounded-full h-6 w-6 border-3 border-white border-t-transparent"></div>
                  Checking In...
                </>
              ) : (
                <>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                  Check In
                </>
              )}
            </button>
          )}

          {/* Make Reservation Button */}
          <button
            onClick={handleMakeReservation}
            className="w-full px-6 py-5 bg-white hover:bg-purple-50 text-purple-600 font-bold text-lg rounded-lg border-2 border-purple-500 shadow-retro hover:shadow-retro-hover transition-all touch-manipulation flex items-center justify-center gap-3"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Make Reservation
          </button>

          {/* View Schedule Button */}
          <button
            onClick={handleViewSchedule}
            className="w-full px-6 py-5 bg-white hover:bg-teal-50 text-teal-600 font-bold text-lg rounded-lg border-2 border-teal-500 shadow-retro hover:shadow-retro-hover transition-all touch-manipulation flex items-center justify-center gap-3"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            View Schedule
          </button>
        </div>

        {/* Divider */}
        <div className="my-8 border-t-2 border-slate-900"></div>

        {/* Additional Board Info */}
        <div className="space-y-3 text-center">
          {board.type && (
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-800 border-2 border-slate-500 rounded-full font-bold text-sm">
              {board.type}
              {board.angle && ` • ${board.angle}°`}
            </div>
          )}
          {board.locationNotes && (
            <p className="text-sm text-slate-600 font-medium">
              📍 {board.locationNotes}
            </p>
          )}
        </div>

        {/* Footer branding */}
        <div className="mt-8 pt-6 border-t border-slate-200 text-center">
          <p className="text-xs text-slate-500 font-medium">
            Powered by <span className="font-bold text-slate-700">BoardRez</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default BoardQuickAccess;
